import { useEffect, useState } from "react";

import InsightsIcon from "@mui/icons-material/Insights";
import LockIcon from "@mui/icons-material/Lock";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { type ForecastRow, type RiskLevel, getCoverageForecast } from "@/api/forecast";
import { listOrganizations } from "@/api/organizations";
import { usePermission } from "@/auth/usePermission";
import { PageHeader } from "@/components/PageHeader";
import { useAuthStore } from "@/store/authStore";

const MONTH_NAMES_FR = [
  "Janvier",
  "Février",
  "Mars",
  "Avril",
  "Mai",
  "Juin",
  "Juillet",
  "Août",
  "Septembre",
  "Octobre",
  "Novembre",
  "Décembre",
];

const RISK_COLOR: Record<RiskLevel, "success" | "info" | "warning" | "error"> = {
  LOW: "success",
  MEDIUM: "info",
  HIGH: "warning",
  CRITICAL: "error",
};

function GapBar({ row }: { row: ForecastRow }) {
  const pct = Math.min(100, Math.round(row.gap_ratio * 100));
  const color =
    pct >= 30 ? "error" : pct >= 15 ? "warning" : pct >= 5 ? "info" : "success";
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 160 }}>
      <Box sx={{ flex: 1 }}>
        <LinearProgress variant="determinate" value={pct} color={color} />
      </Box>
      <Typography variant="caption" sx={{ minWidth: 36, textAlign: "right" }}>
        {pct} %
      </Typography>
    </Stack>
  );
}

export function ForecastPage() {
  const { t } = useTranslation();
  const canRead = usePermission("planning:read");
  const me = useAuthStore((s) => s.me);

  const [orgId, setOrgId] = useState<string>("");
  const [monthsAhead, setMonthsAhead] = useState(3);
  const [lookback, setLookback] = useState(6);

  const orgs = useQuery({
    queryKey: ["organizations-active"],
    queryFn: () => listOrganizations({ status: "ACTIVE", limit: 200 }),
    enabled: canRead,
  });

  useEffect(() => {
    if (!orgId && orgs.data?.items.length) {
      const preferred =
        me?.active_org_id && orgs.data.items.find((o) => o.id === me.active_org_id)
          ? me.active_org_id
          : orgs.data.items[0]?.id ?? "";
      setOrgId(preferred);
    }
  }, [orgs.data, orgId, me?.active_org_id]);

  const forecast = useQuery({
    queryKey: ["coverage-forecast", orgId, monthsAhead, lookback],
    queryFn: () =>
      getCoverageForecast(orgId, { months_ahead: monthsAhead, lookback }),
    enabled: canRead && Boolean(orgId),
  });

  if (!canRead) {
    return (
      <Box>
        <PageHeader title={t("forecast.title")} subtitle={t("forecast.subtitle")} helpTerm="prevision" />
        <Alert severity="warning">{t("forecast.forbidden")}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title={t("forecast.title")}
        subtitle={t("forecast.subtitle")}
        actions={
          <IconButton
            onClick={() => forecast.refetch()}
            aria-label={t("common.retry")}
          >
            <RefreshIcon />
          </IconButton>
        }
      />

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        sx={{ mb: 3, flexWrap: "wrap" }}
      >
        <FormControl size="small" sx={{ minWidth: 260 }}>
          <InputLabel>{t("forecast.organization")}</InputLabel>
          <Select
            label={t("forecast.organization")}
            value={orgId}
            onChange={(e) => setOrgId(e.target.value)}
          >
            {(orgs.data?.items ?? []).map((o) => (
              <MenuItem key={o.id} value={o.id}>
                {o.code} — {o.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>{t("forecast.monthsAhead")}</InputLabel>
          <Select
            label={t("forecast.monthsAhead")}
            value={monthsAhead}
            onChange={(e) => setMonthsAhead(Number(e.target.value))}
          >
            {[1, 2, 3, 6, 12].map((n) => (
              <MenuItem key={n} value={n}>
                {n} {t("forecast.monthsUnit")}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>{t("forecast.lookback")}</InputLabel>
          <Select
            label={t("forecast.lookback")}
            value={lookback}
            onChange={(e) => setLookback(Number(e.target.value))}
          >
            {[3, 6, 12, 18, 24].map((n) => (
              <MenuItem key={n} value={n}>
                {n} {t("forecast.monthsUnit")}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {/* Summary cards */}
      {forecast.data && (
        <Stack direction="row" spacing={1.5} sx={{ mb: 3, flexWrap: "wrap" }}>
          <Paper variant="outlined" sx={{ p: 1.5, minWidth: 200, flex: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {t("forecast.baselineCoverage")}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <InsightsIcon color="primary" />
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {(forecast.data.baseline_coverage_rate * 100).toFixed(1)} %
              </Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {t("forecast.baselineSamples", {
                count: forecast.data.baseline_samples,
              })}
            </Typography>
          </Paper>
          <Paper variant="outlined" sx={{ p: 1.5, minWidth: 200, flex: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {t("forecast.activePractitioners")}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {forecast.data.active_practitioners}
            </Typography>
          </Paper>
        </Stack>
      )}

      {forecast.isLoading ? (
        <CircularProgress />
      ) : forecast.error ? (
        <Alert severity="error">
          {String(
            (forecast.error as { response?: { data?: { detail?: string } } })
              .response?.data?.detail ?? "error",
          )}
        </Alert>
      ) : !forecast.data || forecast.data.rows.length === 0 ? (
        <Alert severity="info">{t("forecast.empty")}</Alert>
      ) : (
        <Paper variant="outlined" sx={{ overflow: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("forecast.col.month")}</TableCell>
                <TableCell align="right">{t("forecast.col.demand")}</TableCell>
                <TableCell align="right">{t("forecast.col.expected")}</TableCell>
                <TableCell align="right">{t("forecast.col.gap")}</TableCell>
                <TableCell sx={{ minWidth: 200 }}>{t("forecast.col.gapBar")}</TableCell>
                <TableCell>{t("forecast.col.risk")}</TableCell>
                <TableCell>{t("forecast.col.status")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {forecast.data.rows.map((r: ForecastRow) => (
                <TableRow key={`${r.year}-${r.month}`} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {MONTH_NAMES_FR[r.month - 1]} {r.year}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{r.demand_slots}</TableCell>
                  <TableCell align="right">{r.expected_filled}</TableCell>
                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      color={r.estimated_gap > 0 ? "error.main" : "text.primary"}
                      sx={{ fontWeight: r.estimated_gap > 0 ? 600 : 400 }}
                    >
                      {r.estimated_gap}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <GapBar row={r} />
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={RISK_COLOR[r.risk_level]}
                      label={t(`forecast.level.${r.risk_level}`)}
                    />
                  </TableCell>
                  <TableCell>
                    {r.is_already_planned ? (
                      <Tooltip title={t("forecast.realisedTooltip")}>
                        <Chip
                          size="small"
                          variant="outlined"
                          icon={<LockIcon fontSize="small" />}
                          label={t("forecast.realised")}
                        />
                      </Tooltip>
                    ) : (
                      <Chip
                        size="small"
                        variant="outlined"
                        color="primary"
                        label={t("forecast.predicted")}
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );
}
